<template>
  <vue-tabs ref="tabs" v-model="activeTab">
    <v-tab :title="t('superuserCustomerDataTabLabel')">
      <template>
        <validation-observer
          ref="customerDataForm"
          v-element-loader="{ endpoint: createEndpoint() }"
          tag="div"
          class="superuser-my-account__tab-container">
          <div class="superuser-my-account__block superuser-my-account__block--border">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserCustomerEditTitle') }}
              <span class="superuser-my-account__block-header-sub">
                {{ selectedUserData.customerNumber }} {{ selectedUserData.name }}
              </span>
            </h2>

            <div class="superuser-my-account__block-content">
              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-number"
                  v-model="selectedUserData.customerNumber"
                  :label-text="`${t('superuserCustomerNumberLabel')}*`"
                  :placeholder="t('superuserCustomerNumberPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  :is-readonly="true"
                  class="superuser-my-account__input"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-name"
                  v-model="selectedUserData.name"
                  :label-text="selectedUserData.isCompany ? `${t('superuserCustomerCompanyNameLabel')}*` : `${t('superuserCustomerNameLabel')}*`"
                  :placeholder="selectedUserData.isCompany ? t('superuserCustomerCompanyNamePlaceholder') : t('superuserCustomerNamePlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                slim>
                <oie-base-input
                  id="superuser-customer-group"
                  v-model="selectedUserData.customerCategory"
                  :label-text="t('superuserCustomerGroupLabel')"
                  :placeholder="t('superuserCustomerGroupPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input"
                  :is-readonly="true"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-phonenumber"
                  v-model="selectedUserData.phone"
                  :label-text="`${t('superuserCustomerPhonenumberLabel')}*`"
                  :placeholder="t('superuserCustomerPhonenumberPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-email"
                  v-model="selectedUserData.email"
                  :label-text="`${t('superuserCustomerEmailLabel')}*`"
                  :placeholder="t('superuserCustomerEmailPlaceHolder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules=""
                slim>
                <oie-base-input
                  id="superuser-customer-specific-delivery-methods"
                  v-model="selectedUserData.customerSpecificDeliveryMethods"
                  :label-text="t('superuserCustomerSpecificDeliveryMethodsLabel')"
                  :placeholder="t('superuserCustomerSpecificDeliveryMethodsPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>

              <oie-base-input
                id="superuser-customer-eod"
                v-model="selectedUserData.customerEOD"
                :label-text="t('superuserCustomerEodLabel')"
                :use-gray-input="true"
                class="superuser-my-account__input-narrow"></oie-base-input>

              <oie-base-input
                id="superuser-customer-vatnumber"
                v-model="selectedUserData.vatNumber"
                :label-text="t('superuserCustomerVatnumberLabel')"
                :placeholder="t('superuserCustomerVatnumberPlaceHolder')"
                :use-gray-input="true"
                class="superuser-my-account__input"
                :is-readonly="!selectedUserData.useVatForCompanies"></oie-base-input>

              <oie-base-input
                id="superuser-customer-alias"
                v-model="selectedUserData.alias"
                :label-text="t('superuserCustomerAliasLabel')"
                :placeholder="t('superuserCustomerAliasPlaceHolder')"
                :use-gray-input="true"
                class="superuser-my-account__input-wide"></oie-base-input>
            </div>
          </div>

          <div class="superuser-my-account__block superuser-my-account__block--border">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserInvoiceAddressTitle') }}
            </h2>

            <div class="superuser-my-account__block-content">
              <oie-base-input
                id="superuser-customer-co"
                v-model="selectedUserData.invoiceAddress.addressCo"
                :label-text="t('superuserCustomerCoLabel')"
                :placeholder="t('superuserCustomerCoPlaceHolder')"
                :use-gray-input="true"
                class="superuser-my-account__input"></oie-base-input>

              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-street"
                  v-model="selectedUserData.invoiceAddress.addressLine1"
                  :label-text="`${t('superuserCustomerStreetLabel')}*`"
                  :placeholder="t('superuserCustomerStreetPlaceHolder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-postal-code"
                  v-model="selectedUserData.invoiceAddress.postalCode"
                  :label-text="`${t('superuserCustomerPostalCodeLabel')}*`"
                  :placeholder="t('superuserCustomerPostalCodePlaceHolder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required"
                slim>
                <oie-base-input
                  id="superuser-customer-city"
                  v-model="selectedUserData.invoiceAddress.city"
                  :label-text="`${t('superuserCustomerCityLabel')}*`"
                  :placeholder="t('superuserCustomerCityPlaceHolder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input"></oie-base-input>
              </validation-provider>
            </div>
          </div>

          <div class="superuser-my-account__block superuser-my-account__block--border">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserCustomerSettingsHeader') }}
            </h2>
            <div class="superuser-my-account__block-content">
              <oie-base-input
                id="comment"
                v-model="selectedUserData.goodsComment"
                :label-text="t('superuserCommentGoodsLabel')"
                :textarea="true"
                :max-length="35"
                :placeholder="t('superuserCommentOnOrderPlaceholder')"></oie-base-input>

              <oie-base-checkbox
                v-model="selectedUserData.hasCustomerPrices"
                size="medium"
                is-for="superuser-customer-price-list"
                :text="t('superuserCustomerHasCustomerPricesLabel')"
                :is-disabled="true">
              </oie-base-checkbox>

              <div class="superuser-my-account__accordion">
                <oie-base-checkbox
                  v-model="selectedUserData.hasInvoice"
                  size="medium"
                  is-for="superuser-customer-invoice"
                  :text="t('superuserCustomerInvoiceLabel')"
                  :class="{ 'superuser-my-account__pdf-checkbox': selectedUserData.hasInvoice }">
                </oie-base-checkbox>
                <oie-base-input
                  v-if="selectedUserData.hasInvoice"
                  id="superuser-customer-idNumber"
                  v-model="selectedUserData.idNumber"
                  :label-text="t('superuserCustomerIdnumberLabel')"
                  :placeholder="t('superuserCustomerIdnumberPlaceHolder')"
                  :use-gray-input="true"
                  class="superuser-my-account__input"></oie-base-input>
              </div>

              <oie-base-checkbox
                v-model="selectedUserData.requireInvoiceReferenceOrderNumber"
                size="medium"
                is-for="superuser-require-invoice-reference-order-number"
                :text="t('superuserCustomerRequireInvoiceRefOrderNoLabel')">
              </oie-base-checkbox>

              <div class="superuser-my-account__accordion">
                <oie-base-checkbox
                  v-model="selectedUserData.pdfInvoice"
                  size="medium"
                  is-for="superuser-customer-pdf-invoice"
                  :text="t('superuserCustomerPdfInvoiceLabel')"
                  :class="{ 'superuser-my-account__pdf-checkbox': selectedUserData.pdfInvoice }">
                </oie-base-checkbox>

                <validation-provider
                  v-if="selectedUserData.pdfInvoice"
                  v-slot="{errors}"
                  rules="required"
                  slim>
                  <oie-base-input
                    id="superuser-customer-pdf-invoice-email"
                    v-model="selectedUserData.pdfInvoiceEmail"
                    :label-text="`${t('superuserCustomerPdfInvoiceEmailLabel')}*`"
                    :placeholder="t('superuserCustomerPdfInvoiceEmailPlaceholder')"
                    :error-messages="errors"
                    :use-gray-input="true"
                    class="superuser-my-account__input"></oie-base-input>
                </validation-provider>
              </div>

              <div class="superuser-my-account__accordion">
                <div class="superuser-my-account__accordion">
                  <oie-base-checkbox
                    v-model="hasPreferredDeliveryMethod"
                    size="medium"
                    is-for="superuser-customer-delivery"
                    text="Transportör"
                    :class="{ 'superuser-my-account__pdf-checkbox': hasPreferredDeliveryMethod }"
                    @input="hasPreferredDeliveryMethodChange">
                  </oie-base-checkbox>
                  <div v-if="hasPreferredDeliveryMethod" class="superuser-my-account__block-content">
                    <oie-base-dropdown
                      v-model="selectedUserData.preferredDeliveryMethod"
                      label-text="Transportör"
                      :options="deliveryMethods"
                      name="superuser-select-delivery-method"
                      :initial-text="deliveryTypeText"
                      :use-thin-border="true"
                      class="superuser-my-account__deliverymethod-select"
                      container-classes="superuser-my-account__deliverymethod-select-container"
                      z-index="4">
                    </oie-base-dropdown>
                  </div>
                  <div v-if="hasPreferredDeliveryMethod" class="superuser-my-account__block-content superuser-my-account__deliveryagent">
                    <oie-base-dropdown
                      v-if="deliveryAgents && deliveryAgents.length > 0"
                      v-model="selectedUserData.preferredDeliveryAgent"
                      label-text="Ombud"
                      :options="deliveryAgents"
                      name="superuser-select-delivery-agent"
                      :initial-text="deliveryAgentText"
                      :use-thin-border="true"
                      class="superuser-my-account__deliveryagent-select"
                      container-classes="superuser-my-account__deliveryagent-select-container">
                    </oie-base-dropdown>
                  </div>
                </div>
              </div>

              <oie-base-checkbox
                v-model="selectedUserData.isCompany"
                size="medium"
                is-for="superuser-customer-is-company"
                :text="t('superuserCustomerIsCompany')"
                :is-disabled="selectedUserData.useVatForCompanies">
              </oie-base-checkbox>

              <oie-base-checkbox
                v-model="selectedUserData.isShipCustomer"
                size="medium"
                is-for="superuser-customer-is-ship-customer"
                :text="t('superuserIsShipCustomer')">
              </oie-base-checkbox>

              <oie-base-checkbox
                v-model="selectedUserData.isKlarnaBlocked"
                size="medium"
                is-for="superuser-customer-is-klarna-blocked"
                :text="t('superuserIsKlarnaBlocked')">
              </oie-base-checkbox>

              <oie-base-dropdown
                v-model="selectedUserData.language"
                :label-text="tStatic('superUserUserPreferredLanguage')"
                :options="selectedUserData.marketLanguages"
                name="user-language"
                :use-filter-functionality="false"
                :default-selected="selectedUserData.language"
                container-classes="">
              </oie-base-dropdown>
            </div>

            <div class="superuser-my-account__block-content">
            </div>

            <div class="superuser-my-account__block-content">
              <oie-base-input
                id="sucomment"
                v-model="selectedUserData.superuserNote"
                :label-text="t('superuserNoteLabel')"
                :textarea="true"
                :max-length="200"
                :placeholder="t('superuserNotePlaceholder')"></oie-base-input>
            </div>

            <div class="superuser-my-account__block-content">
            </div>
          </div>

          <div class="superuser-my-account__block superuser-my-account__block--margin-bottom">
            <div class="superuser-my-account__block-content superuser-my-account__block-content--sync-info">
              <div class="superuser-my-account__row">
                <div class="superuser-my-account__key">
                  {{ t('superuserCustomerIsSynedLabel') }}
                </div>
                <div class="superuser-my-account__value">
                  {{ selectedUserData.isSynced ? tStatic('genericYes') : tStatic('genericNo') }}
                </div>
              </div>

              <div class="superuser-my-account__row">
                <div class="superuser-my-account__key">
                  {{ t('superuserCustomerLastModifiedLabel') }}
                </div>
                <div class="superuser-my-account__value">
                  {{ selectedUserData.lastModified }}
                </div>
              </div>
            </div>
          </div>

          <div class="superuser-my-account__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button class="button--true-ghost" :text="t('superuserSaveButtonLabel')" @click="saveUser()"></oie-base-button>
            <oie-base-button class="button--with-border button--blue" :text="t('superuserSaveAndCloseButtonLabel')" @click="saveUser(true)"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>

    <v-tab v-if="!selectedUserData.internalCustomer" :title="t('superuserCustomerCredentialsTabLabel')">
      <template>
        <validation-observer
          ref="customerCredentialsForm"
          v-element-loader="{ endpoint: [createEndpoint('password'), createEndpoint('forgotPassword')] }"
          tag="div"
          class="superuser-my-account__tab-container">
          <div class="superuser-my-account__block">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserCustomerCredentialsTitle') }}
              <span class="superuser-my-account__block-header-sub">
                {{ selectedUserData.customerNumber }} {{ selectedUserData.name }}
              </span>
            </h2>
            <div class="superuser-my-account__block-content">
              <validation-provider
                v-slot="{errors}"
                vid="pw"
                rules="required|passwordPolicy"
                slim>
                <oie-base-input
                  id="superuser-customer-new-password"
                  v-model="newPassword"
                  :label-text="`${t('superuserCustomerNewPasswordLabel')}*`"
                  :placeholder="t('superuserCustomerNewPasswordPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required|password:pw"
                slim>
                <oie-base-input
                  id="superuser-customer-new-password-repeat"
                  v-model="newPasswordRepeated"
                  :label-text="`${t('superuserCustomerNewPasswordRepeatLabel')}*`"
                  :placeholder="t('superuserCustomerNewPasswordRepeatPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>
            </div>
          </div>

          <div class="superuser-my-account__block-content superuser-my-account__block--margin-bottom">
            <button type="button" class="superuser-my-account__link" @click="resetPassword()">
              {{ t('superuserSendResetLinkButtonLabel') }}
            </button>
          </div>

          <div class="superuser-my-account__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button class="button--true-ghost" :text="t('superuserSaveButtonLabel')" @click="savePassword()"></oie-base-button>
            <oie-base-button class="button--with-border button--blue" :text="t('superuserSaveAndCloseButtonLabel')" @click="savePassword(true)"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>

    <v-tab v-if="!selectedUserData.internalCustomer" :title="t('superuserCustomerChangeUsernameTabLabel')">
      <template>
        <validation-observer
          ref="customerChangeUsernameForm"
          v-element-loader="{ endpoint: [createEndpoint('username')] }"
          tag="div"
          class="superuser-my-account__tab-container">
          <div class="superuser-my-account__block">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserCustomerChangeUsernameTitle') }}
              <span class="superuser-my-account__block-header-sub">
                {{ selectedUserData.customerNumber }} {{ selectedUserData.name }}
              </span>
            </h2>
            <div class="superuser-my-account__block-content">
              <oie-base-input
                id="superuser-customer-new-username-current"
                v-model="selectedUserData.userId"
                :label-text="t('superuserCustomerChangeUsernameCurrentLabel')"
                :is-readonly="true"
                :use-gray-input="true"
                class="superuser-my-account__input-wide"></oie-base-input>

              <validation-provider
                v-slot="{errors}"
                vid="newUsername"
                rules="required|email"
                slim>
                <oie-base-input
                  id="superuser-customer-new-username"
                  v-model="newUsername"
                  :label-text="`${t('superuserCustomerChangeUsernameNewLabel')}*`"
                  :placeholder="t('superuserCustomerChangeUsernameNewPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>
            </div>
          </div>

          <div class="superuser-my-account__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button class="button--true-ghost" :text="t('superuserSaveButtonLabel')" @click="changeUsername()"></oie-base-button>
            <oie-base-button class="button--with-border button--blue" :text="t('superuserSaveAndCloseButtonLabel')" @click="changeUsername(true)"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>

    <v-tab v-if="selectedUserData.internalCustomer" :title="t('superuserCustomerCreateLoginTabLabel')">
      <template>
        <validation-observer
          ref="customerCreateLoginForm"
          v-element-loader="{ endpoint: [createEndpoint('login')] }"
          tag="div"
          class="superuser-my-account__tab-container">
          <div class="superuser-my-account__block">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserCustomerCreateLoginTitle') }}
              <span class="superuser-my-account__block-header-sub">
                {{ selectedUserData.customerNumber }} {{ selectedUserData.name }}
              </span>
            </h2>
            <div class="superuser-my-account__block-content">
              <validation-provider
                v-slot="{errors}"
                vid="createLoginEmail"
                rules="required|email"
                slim>
                <oie-base-input
                  id="superuser-customer-create-login-email"
                  v-model="createLoginEmail"
                  :label-text="`${t('superuserCustomerCreateLoginNewEmailLabel')}*`"
                  :placeholder="t('superuserCustomerCreateLoginNewEmailPlaceholder')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>
            </div>
          </div>

          <div class="superuser-my-account__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button class="button--with-border button--blue" :text="t('superuserCustomerCreateLoginCreateButtonLabel')" @click="createLogin(true)"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>

    <v-tab v-if="!selectedUserData.internalCustomer" :title="t('superuserChangeCustomerNumberTabLabel')">
      <template>
        <validation-observer
          ref="customerChangeCustomerNumberForm"
          v-element-loader="{ endpoint: [createEndpoint('username')] }"
          tag="div"
          class="superuser-my-account__tab-container">
          <div class="superuser-my-account__block">
            <h2 class="superuser-my-account__block-header">
              {{ t('superuserChangeNumberTitle') }}
              <span class="superuser-my-account__block-header-sub">
                {{ selectedUserData.customerNumber }} {{ selectedUserData.name }}
              </span>
            </h2>
            <div class="superuser-my-account__block-content">
              <oie-base-input
                id="superuser-customer-new-username-current"
                v-model="selectedUserData.customerNumber"
                :label-text="t('superuserChangeCustomerNumberCurrentLabel')"
                :is-readonly="true"
                :use-gray-input="true"
                class="superuser-my-account__input-wide"></oie-base-input>

              <validation-provider
                v-slot="{errors}"
                vid="newUsername"
                rules="required|numeric"
                slim>
                <oie-base-input
                  id="superuser-customer-new-customer-number"
                  v-model="newCustomerNumber"
                  :label-text="`${t('superuserChangeNewCustomerNumberLabel')}*`"
                  :placeholder="t('superuserChangeNewCustomerNumberPlaceHodler')"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-my-account__input-wide"></oie-base-input>
              </validation-provider>
            </div>
          </div>

          <div class="superuser-my-account__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button class="button--with-border button--blue" :text="t('superuserSaveAndCloseButtonLabel')" @click="changeCustomerNumber(true)"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>

    <v-tab :title="t('superuserCustomerDeliveryAgreementTabLabel')">
      <validation-observer
        ref="customerAgreements"
        tag="div"
        class="superuser-my-account__tab-container">
        <div class="superuser-my-account__block">
          <h2 class="superuser-my-account__block-header">
            {{ t('superuserCustomerDeliveryAgreementTitleLabel') }}
          </h2>

          <div class="superuser-my-account__block-content">
            <oie-base-dropdown
              v-model="deliveryProvider"
              :label-text="t('superuserCustomerDeliveryAgreementSelectDeliveryProvider')"
              :options="deliveryProviders"
              name="superuser-select-shipping-provider"
              :initial-text="t('superuserCustomerDeliveryAgreementSelectDeliveryProviderInitialText')"
              :use-thin-border="true"
              class="superuser-my-account__provider-select"
              container-classes="superuser-my-account__provider-select-container">
            </oie-base-dropdown>

            <validation-provider v-slot="{errors}" rules="required" slim>
              <oie-base-input
                id="superuser-customer-shipping-deal-id"
                v-model="deliveryProviderId"
                :label-text="`${t('superuserCustomerDeliveryAgreementProviderIdLabel')}*`"
                :placeholder="t('superuserCustomerDeliveryAgreementProviderIdPlaceholder')"
                :error-messages="errors"
                :use-gray-input="true"
                class="superuser-my-account__input-wide"></oie-base-input>
            </validation-provider>

            <oie-base-button class="button--with-border button--blue supseruser-impersonate__action" :text="t('superuserAddCustomerDeliveryAgreementButtonLabel')" @click="addDeliveryAgreement()"></oie-base-button>
          </div>
        </div>

        <h3 class="superuser-my-account__block-header">
          Avtal
        </h3>

        <ul :class="['edit-remove-list', { 'edit-remove-list--emtpy-state': customerDeliveryAgreements.length === 0 }]">
          <li v-if="customerDeliveryAgreements.length === 0" class="edit-remove-list__emtpy-list">
            {{ t('superuserNoCustomerDeliveryAgreementsText') }}
            <!-- Inga avtal -->
          </li>

          <li v-for="agreement of customerDeliveryAgreements" :key="agreement.customerNumberAtCarrier">
            <div>
              {{ agreement.carrier }} - {{ agreement.customerNumberAtCarrier }} - {{ agreement.forwarderCode }}

              <div class="edit-remove-list__actions">
                <button class="edit-remove-list__action" @click="removeDeliveryAgreement(agreement.carrier)">
                  <oie-use-svg svg="trashcan"></oie-use-svg>
                </button>
              </div>
            </div>
          </li>
        </ul>

        <div class="superuser-my-account__action-block">
          <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
        </div>
      </validation-observer>
    </v-tab>

    <v-tab :title="t('superuserCustomerRemoveTabLabel')">
      <validation-observer
        ref="removeCustomerForm"
        tag="div"
        class="superuser-my-account__tab-container">
        <div class="superuser-my-account__block">
          <h2 class="superuser-my-account__block-header">
            {{ t('superuserCustomerRemoveTitle') }}
          </h2>
          <div v-html="t('superuserCustomerRemove')"></div>
          <div class="superuser-my-account__block-content">
            <validation-provider
              v-slot="{errors}"
              vid="removeCustomerEmail"
              rules="required|email"
              slim>
              <oie-base-input
                id="superuser-remove-customer"
                v-model="removeCustomerEmail"
                :label-text="`${t('superuserCustomerRemoveEmail')}*`"
                :placeholder="selectedUserData.userId"
                :error-messages="errors"
                :use-gray-input="true"
                class="superuser-my-account__input-wide"></oie-base-input>
            </validation-provider>
          </div>
        </div>

        <div class="superuser-my-account__action-block">
          <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
          <oie-base-button class="button--with-border button--red" :text="t('superuserCustomerRemoveButtonLabel')" @click="removeCustomer(true)"></oie-base-button>
        </div>
      </validation-observer>
    </v-tab>
  </vue-tabs>
</template>

<script>
import { VueTabs, VTab } from 'vue-nav-tabs/dist/vue-tabs.js'
import httpService from '@/core/http-service'
import _ from 'lodash'

export default {
  components: {
    VueTabs,
    VTab
  },
  props: {
    selectedUserData: {
      type: Object,
      required: true
    },
    selectedUserIndex: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    activeTab: null,
    selectedUserDataOrigin: null,
    newPassword: null,
    newPasswordRepeated: null,
    newUsername: null,
    createLoginEmail: null,
    removeCustomerEmail: null,
    deliveryProviders: null,
    deliveryProvider: null,
    deliveryProviderId: null,
    customerDeliveryAgreements: [],
    deliveryMethods: null,
    deliveryAgents: null,
    hasPreferredDeliveryMethod: false
  }),
  computed: {
    tabsRef() {
      return this.$refs.tabs
    },
    isCustomerDataTabActive() {
      return this.tabsRef.activeTabIndex === 0
    },
    isCustomerCredentialsTabActive() {
      return this.tabsRef.activeTabIndex === 1
    },
    isCustomerUsernameTabActive() {
      return this.tabsRef.activeTabIndex === 2
    },
    deliveryTypeText() {
      if (this.selectedUserData && this.selectedUserData.preferredDeliveryMethod && this.deliveryMethods) {
        var delivery = this.deliveryMethods.find(m => m.value === this.selectedUserData.preferredDeliveryMethod)
        return delivery && delivery.text
      }
      return 'Välj leveranssätt'
    },
    deliveryAgentText() {
      if (this.selectedUserData && this.selectedUserData.preferredDeliveryAgent && this.deliveryAgents) {
        var agent = this.deliveryAgents.find(m => m.value === this.selectedUserData.preferredDeliveryAgent)
        return agent && agent.text
      }
      return 'Välj ombud'
    }
  },
  watch: {
    hasPreferredDeliveryMethod() {
      if (this.hasPreferredDeliveryMethod && this.deliveryMethods === null) {
        this.getDeliveryMethods()
        this.getAgents(this.selectedUserData.preferredDeliveryMethod)
      }
    },
    'selectedUserData.preferredDeliveryMethod': function (newVal, oldVal) {
      if (newVal) {
        this.selectedUserData.preferredDeliveryAgent = null
        this.getAgents(newVal)
      }
    }
  },
  mounted() {
    this.freezeCopySelectedUser()
    httpService.get('superuser/carrier').then((data) => {
      this.deliveryProviders = data.data
    })
    httpService.get(this.createEndpoint('deliveryAgreements')).then((data) => {
      this.customerDeliveryAgreements = data.data
    })
    if (this.selectedUserData.preferredDeliveryMethod) {
      this.hasPreferredDeliveryMethod = true
    }
  },
  methods: {
    createEndpoint(action) {
      let endpoint = `superuser/user/${this.selectedUserData.id}`
      if (action !== undefined) {
        endpoint += `/${action}`
      }

      return endpoint
    },
    resetModels() {
      this.selectedUserDataOrigin = null
      this.newPassword = null
      this.newPasswordRepeated = null
      this.newUsername = null
    },
    resetSelectedUserData() {
      this.publish('superuser-reset-user-data', { originalValues: this.selectedUserDataOrigin, index: this.selectedUserIndex })
    },
    updatedSelectedUserData(user) {
      this.publish('superuser-user-updated', { user: user, index: this.selectedUserIndex })
    },
    updateUserAndReset(user) {
      this.publish('superuser-update-and-reset-user-data', { user: user })
    },
    freezeCopySelectedUser() {
      this.selectedUserDataOrigin = _.cloneDeep(this.selectedUserData)
    },
    getDeliveryMethods() {
      httpService.get(this.createEndpoint(`deliveryMethods`)).then((data) => {
        this.deliveryMethods = data.data
      })
    },
    getAgents(optionId) {
      httpService.get(this.createEndpoint(`deliveryMethods/${optionId}/agents`)).then((data) => {
        this.deliveryAgents = data.data
      })
    },
    hasPreferredDeliveryMethodChange(value) {
      if (!value) {
        this.selectedUserData.preferredDeliveryMethod = null
        this.selectedUserData.preferredDeliveryAgent = null
        this.deliveryAgents = null
      }
    },
    cancelEdit() {
      if ((this.isCustomerDataTabActive && !_.isEqual(this.selectedUserData, this.selectedUserDataOrigin)) ||
      (this.isCustomerCredentialsTabActive && (this.newPassword || this.newPasswordRepeated)) ||
      (this.isCustomerUsernameTabActive && this.newUsername && this.selectedUserDataOrigin.userId !== this.newUsername)) {
        this.triggerModalCancel()
      } else {
        this.resetSelectedUserData()
        this.resetModels()
      }
    },
    async saveUser(closeEdit) {
      const isValid = await this.$refs.customerDataForm.validate()

      if (!isValid) {
        return
      }

      var validCheck
      var warnMessage
      this.selectedUserData.preCheck = true
      await httpService.patch(this.createEndpoint(), this.selectedUserData).then((response) => {
        if (!response.data.success) {
          validCheck = false
          warnMessage = response.data.message
        }else {
          validCheck = true
        }
      }).catch((e) => {
        validCheck = false
      })

      if(!validCheck) {
        await new Promise((resolve, reject) => {
          this.triggerModal(warnMessage, resolve, reject)
        })
      }
      await this.saveUserData(closeEdit)
    },
    async saveUserData(closeEdit) {
      this.selectedUserData.preCheck = false
      httpService.patch(this.createEndpoint(), this.selectedUserData).then((response) => {
        this.selectedUserData.isSynced = response.data.isSynced
        this.selectedUserData.lastModified = response.data.lastModified
        this.freezeCopySelectedUser()

        if (closeEdit) {
          this.updateUserAndReset(response.data)
          this.resetModels()
        } else {
          this.updatedSelectedUserData(response.data)
        }
      })
    },
    async savePassword(closeEdit) {
      const isValid = await this.$refs.customerCredentialsForm.validate()

      if (!isValid) {
        return
      }

      httpService.patch(this.createEndpoint('password'), { newPassword: this.newPassword }).then((response) => {
        this.freezeCopySelectedUser()
        this.newPassword = null
        this.newPasswordRepeat = null
        this.$refs.customerCredentialsForm.reset()

        if (closeEdit) {
          this.resetSelectedUserData()
          this.resetModels()
        }
      })
    },
    resetPassword() {
      httpService.post(this.createEndpoint('forgotPassword'))
    },
    async changeUsername(closeEdit) {
      const isValid = await this.$refs.customerChangeUsernameForm.validate()

      if (!isValid) {
        return
      }

      httpService.patch(this.createEndpoint('username'), { newUserName: this.newUsername }).then((response) => {
        this.selectedUserData.userId = response.data.userId
        this.freezeCopySelectedUser()
        this.newUsername = null
        this.$refs.customerChangeUsernameForm.reset()

        if (closeEdit) {
          this.resetSelectedUserData()
          this.resetModels()
        }
      }).catch((error) => {
        if (error.response.status === 409) {
          this.$refs.customerChangeUsernameForm.setErrors(error.response.data.errors)
        }
      })
    },
    async createLogin(closeEdit) {
      const isValid = await this.$refs.customerCreateLoginForm.validate()

      if (!isValid) {
        return
      }

      httpService.post(this.createEndpoint('login'), { email: this.createLoginEmail }).then((response) => {
        this.selectedUserData.userId = response.data.userId
        this.selectedUserData.email = response.data.email
        this.selectedUserData.internalCustomer = response.data.internalCustomer
        this.freezeCopySelectedUser()
        this.createLoginEmail = null
        this.$refs.customerCreateLoginForm.reset()

        if (closeEdit) {
          this.resetSelectedUserData()
          this.resetModels()
        }
      }).catch((error) => {
        if (error.response.status === 409) {
          this.$refs.customerCreateLoginForm.setErrors(error.response.data.errors)
        }
      })
    },
    async changeCustomerNumber(closeEdit) {
      const isValid = await this.$refs.customerChangeCustomerNumberForm.validate()

      if (!isValid) {
        return
      }

      httpService.patch(this.createEndpoint('customerNumber'), { newCustomerNumber: this.newCustomerNumber }).then((response) => {
        this.selectedUserData.userId = response.data.userId
        this.freezeCopySelectedUser()
        this.newCustomerNumber = null
        this.$refs.customerChangeCustomerNumberForm.reset()

        if (closeEdit) {
          this.resetSelectedUserData()
          this.resetModels()
        }
      }).catch((error) => {
        if (error.response.status === 409) {
          this.$refs.customerChangeCustomerNumberForm.setErrors(error.response.data.errors)
        }
      })
    },
    async removeCustomer(closeEdit) {
      const isValid = await this.$refs.removeCustomerForm.validate()

      if (!isValid) {
        return
      }

      httpService.deleteWithBody(this.createEndpoint(), { confirmEmail: this.removeCustomerEmail }).then((response) => {
        this.publish('superuser-user-removed', this.selectedUserData.userId)
        this.freezeCopySelectedUser()
        this.removeCustomerEmail = null
        this.$refs.removeCustomerForm.reset()

        if (closeEdit) {
          this.resetSelectedUserData()
          this.resetModels()
        }
      }).catch((error) => {
        if (error.response.status === 400) {
          this.$refs.removeCustomerForm.setErrors(error.response.data.errors)
        }
      })
    },
    triggerModal(message, resolve, reject) {
      this.publish('modal.show.generic', {
        header: this.tStatic('superUserWarningMessageHeader'),
        text: message,
        buttons: [ {
          text: this.tStatic('superUserContinueButton'),
          usePrimaryButton: true,
          handler: () => {
            this.publish('modal.hide.generic')
            resolve()
          }
        }]
      })
    },
    triggerModalCancel() {
      this.publish('modal.show.generic', {
        header: this.t('superuserFormHasUnsavedDataHeader'),
        text: this.t('superuserFormHasUnsavedDataText'),
        buttons: [ {
          text: this.t('superuserContinueButtonLabel'),
          usePrimaryButton: true,
          handler: () => {
            this.resetSelectedUserData()
            this.resetModels()
            this.$modal.hide('generic')
          }
        }]
      })
    },
    async addDeliveryAgreement() {
      const form = this.$refs.customerAgreements
      const isValid = await form.validate()

      console.dir(form)

      if (!isValid) {
        return
      }

      let deliveryProviderLocal = this.deliveryProvider
      var agreement = _.find(this.customerDeliveryAgreements, function(o) { return o.carrier === deliveryProviderLocal })
      var deliveryProviderObj = _.find(this.deliveryProviders, function(o) { return o.value === deliveryProviderLocal })

      httpService.post(this.createEndpoint('deliveryAgreement'), { agreement: { carrier: this.deliveryProvider, customerNumberAtCarrier: this.deliveryProviderId, forwarderCode: deliveryProviderObj.code } }).then((data) => {
        if(agreement) {
          agreement.customerNumberAtCarrier = this.deliveryProviderId
        } else {
          this.customerDeliveryAgreements.push({
            carrier: this.deliveryProvider,
            customerNumberAtCarrier: this.deliveryProviderId
          })
        }

        this.deliveryProvider = null
        this.deliveryProviderId = null

        form.reset()
      })
    },
    async removeDeliveryAgreement(carrier) {
      httpService.delete(this.createEndpoint('deliveryAgreement'), { carrier: carrier }).then((data) => {
        var index = _.findIndex(this.customerDeliveryAgreements, (o) => { return o.carrier === carrier })
        this.customerDeliveryAgreements.splice(index, 1)
      })
    },
    async editDeliveryAgreement(carrier, customerNumberAtCarrier) {

    }
  }
}
</script>

<style lang="scss">
.superuser-my-account__block {
  padding: measure(full) 0;

  &:first-child {
    padding-top: 0;
  }
}

.superuser-my-account__block--border {
  border-bottom: 1px solid color(border-gray);
}

.superuser-my-account__block--margin-bottom {
  margin-bottom: measure(double);
}

.superuser-my-account__block-content {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: measure(half);
}

.superuser-my-account__block-content--sync-info {
  flex-direction: column;

  @include media(small) {
    flex-direction: row;
  }
}

.superuser-my-account__block-header {
  @include body-bold;
  margin-top: 0;
}

.superuser-my-account__block-header-sub {
  @include body;
}

.superuser-my-account__input {
  flex: 1 0 11rem;
}

.superuser-my-account__input-wide {
  flex: 4 0 18rem;
  max-width: 31rem;
}

.superuser-my-account__input-narrow {
  flex: 1 0 11rem;
  max-width: 10rem;
}

.superuser-my-account__input-checkbox {
  flex-shrink: 0;
  flex-basis: auto;
}

.superuser-my-account__input-filler {
  flex-basis: 11rem;
}

.superuser-my-account__action-block {
  display: flex;
  justify-content: flex-end;
  padding: measure(quarter) measure(half);
  border: 1px solid color(border-gray);
}

.superuser-my-account__action-block > * {
  @include body-smaller-semi-bold;

  &:first-child {
    @include media(small) {
      margin-right: auto;
      padding-left: 0;
    }
  }
}

.superuser-my-account__link {
  @include reset-button;
  @include reset-link;
}

.superuser-my-account__pdf-checkbox {
  margin-bottom: measure(quarter);
}

.superuser-my-account__row {
  display: flex;
  flex-direction: column;
}

.superuser-my-account__key {
  @include body-smaller-semi-bold;
  padding-right: measure(quarter);
}

.superuser-my-account__provider-select,
.superuser-my-account__deliverymethod-select,
.superuser-my-account__deliveryagent-select {
  min-width: 20rem;
}

.superuser-my-account__provider-select-container,
.superuser-my-account__deliverymethod-select-container
.superuser-my-account__deliveryagent-select-container {
  background-color: color(background-gray-5);
}

.superuser-my-account__deliveryagent {
  margin-top: measure(quarter);
}

.edit-remove-list {
  margin-bottom: measure(double);
  padding-left: measure(full);
}

.edit-remove-list--emtpy-state {
  list-style-type: none;
  padding: 0;
}

.edit-remove-list > li:not(:last-child) {
  margin-bottom: measure(quarter);
}

.edit-remove-list__emtpy-list {
  font-style: italic;
  list-style-type: none;
}

.edit-remove-list > li > div {
  display: inline-flex;
  align-items: center;
}

.edit-remove-list__actions {
  display: flex;
  margin-left: measure(double);
}

.edit-remove-list__action {
  @include reset-button;
  padding: measure(eighth)
}

.edit-remove-list__action:first-child {
  margin-right: measure(quarter);
}

</style>
